// src/pages/auth/RegisterPage.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerWithEmail, loginWithGoogle } from "../../services/authService";
import { createUserProfile } from "../../services/firestoreService";
import Button from "../../components/ui/Button";
import Input  from "../../components/ui/Input";
import styles from "./AuthPages.module.css";

const ROLES = [
  {
    id:    "freelancer",
    label: "Freelancer",
    desc:  "I'm looking for gigs and freelance work",
    icon:  "🧑‍💻",
  },
  {
    id:    "admin",
    label: "Admin / Employer",
    desc:  "I post jobs and manage listings",
    icon:  "🏢",
  },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [role,     setRole]     = useState("freelancer");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const validate = () => {
    if (!name.trim())           return "Full name is required.";
    if (password.length < 6)   return "Password must be at least 6 characters.";
    if (password !== confirm)  return "Passwords do not match.";
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError(""); setLoading(true);
    try {
      const { user, role: selectedRole } = await registerWithEmail(email, password, name, role);
      await createUserProfile(user, selectedRole);
      navigate("/dashboard");
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try {
      await loginWithGoogle();
      // The user will be redirected – the actual profile creation & navigation
      // happens after redirect via the RedirectHandler in App.jsx.
      // For now, just initiate redirect.
    } catch (err) {
      setError(friendlyError(err.code));
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.logo}>GS</span>
          <h1 className={styles.title}>Gigscout</h1>
        </div>
        <h2 className={styles.heading}>Create your account</h2>
        <p className={styles.sub}>Start finding gigs today</p>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.roleRow}>
          {ROLES.map(r => (
            <button
              key={r.id}
              type="button"
              className={`${styles.roleCard} ${role === r.id ? styles.roleCardActive : ""}`}
              onClick={() => setRole(r.id)}
            >
              <span className={styles.roleIcon}>{r.icon}</span>
              <span className={styles.roleLabel}>{r.label}</span>
              <span className={styles.roleDesc}>{r.desc}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleRegister} className={styles.form}>
          <Input
            label="Full Name" id="name" type="text"
            value={name} onChange={e => setName(e.target.value)}
            placeholder="Jane Doe" required
          />
          <Input
            label="Email" id="email" type="email"
            value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com" required
          />
          <Input
            label="Password" id="password" type="password"
            value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Min. 6 characters" required
          />
          <Input
            label="Confirm Password" id="confirm" type="password"
            value={confirm} onChange={e => setConfirm(e.target.value)}
            placeholder="Repeat password" required
          />
          <Button type="submit" fullWidth loading={loading}>
            Create {role === "admin" ? "Admin" : "Freelancer"} Account
          </Button>
        </form>

        <div className={styles.divider}><span>or</span></div>
        <Button variant="secondary" fullWidth onClick={handleGoogle} disabled={loading}>
          <GoogleIcon /> Continue with Google
        </Button>
        <p className={styles.footer}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
    </svg>
  );
}

function friendlyError(code) {
  switch (code) {
    case "auth/email-already-in-use": return "That email is already registered.";
    case "auth/invalid-email":        return "Please enter a valid email address.";
    case "auth/weak-password":        return "Password is too weak.";
    default: return "Something went wrong. Please try again.";
  }
}